import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaEdit, FaEnvelope, FaPlus, FaSearch, FaTimes, FaTrash } from "react-icons/fa";
import { apihubDelete, apihubGet, apihubPost, apihubPut } from "../../api/apihub";
import { businessPath } from "../../config/businessMenu";
import { useProjectPhone } from "../../hooks/useProjectPhone";
import { hasPermission } from "../../utils/auth";
import StatusCapsule from "../StatusCapsule";

const emptyForm = () => ({
  templateName: "",
  senderid: "",
  selectedProviderId: "",
  templateMessage: "",
});

const AllSmsTemplate = () => {
  const { projectId } = useProjectPhone();
  const canCreate = hasPermission("SmsTemplate_Create");
  const canOpenSmsConfig = hasPermission("SMSConfig_Read");

  const [templates, setTemplates] = useState([]);
  const [providers, setProviders] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const fetchTemplates = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const q = encodeURIComponent(search.trim());
      const res = await apihubGet(
        `/getInteractedCustomerDetail/smstemplatebyid/${projectId}?page=${page}&limit=${limit}&q=${q}`,
      );
      setTemplates(res?.data || []);
      setPagination({
        total: res?.pagination?.total ?? 0,
        totalPages: res?.pagination?.totalPages ?? 1,
      });
    } catch (err) {
      setError(err.message || "Failed to load SMS templates");
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, page, limit, search]);

  const fetchProviders = useCallback(async () => {
    if (!projectId) return;
    try {
      const res = await apihubGet(
        `/getInteractedCustomerDetail/fetchprovidername/${projectId}`,
      );
      setProviders(Array.isArray(res?.data) ? res.data : []);
    } catch {
      setProviders([]);
    }
  }, [projectId]);

  useEffect(() => {
    const timer = setTimeout(fetchTemplates, 300);
    return () => clearTimeout(timer);
  }, [fetchTemplates]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const openCreate = () => {
    if (!canCreate) return;
    setEditId(null);
    setForm(emptyForm());
    setFormError("");
    setModalOpen(true);
  };

  const openEdit = (row) => {
    if (!canCreate) return;
    setEditId(row._id);
    setForm({
      templateName: row.templateName || row.name || "",
      senderid: row.senderId || row.senderid || "",
      selectedProviderId: row.sms_config_id || row.smsConfigId || "",
      templateMessage: row.message || row.body || "",
    });
    setFormError("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditId(null);
    setForm(emptyForm());
    setFormError("");
  };

  const onFieldChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === "senderid" ? value.toUpperCase() : value,
    }));
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!projectId) return;

    const templateName = form.templateName.trim();
    const templateMessage = form.templateMessage.trim();
    const senderid = form.senderid.trim();

    if (!templateName || !templateMessage || !senderid || !form.selectedProviderId) {
      setFormError("Please fill all required fields.");
      return;
    }

    setSaving(true);
    setFormError("");
    try {
      const payload = {
        projectid: projectId,
        smstemplatename: templateName,
        smstemplatemessage: templateMessage,
        senderid,
        sms_config_id: form.selectedProviderId,
        selectedProviderId: form.selectedProviderId,
      };

      const res = editId
        ? await apihubPut(
            `/getInteractedCustomerDetail/updatesmstemplate/${editId}`,
            {
              ...payload,
              editedSenderId: senderid,
              providerId: form.selectedProviderId,
            },
          )
        : await apihubPost(
            "/getInteractedCustomerDetail/createsmstemplate",
            payload,
          );

      if (res?.success === false) {
        throw new Error(res.message || "Save failed");
      }

      setMessage(res?.message || "SMS template saved successfully");
      setModalOpen(false);
      setEditId(null);
      setForm(emptyForm());
      setFormError("");
      fetchTemplates();
    } catch (err) {
      setFormError(err.message || "Failed to save SMS template");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!canCreate || !window.confirm("Delete this SMS template?")) return;
    setError("");
    try {
      const res = await apihubDelete(
        `/getInteractedCustomerDetail/deletesmstemplate/${id}`,
      );
      if (res?.success === false) {
        throw new Error(res.message || "Delete failed");
      }
      setMessage(res?.message || "SMS template deleted");
      fetchTemplates();
    } catch (err) {
      setError(err.message || "Failed to delete SMS template");
    }
  };

  return (
    <div className="biz-panel-page">
      <div className="biz-panel-header biz-panel-header-row">
        <h1>
          <FaEnvelope />
          SMS Templates
        </h1>
        <button
          type="button"
          className="biz-btn-primary"
          onClick={openCreate}
          disabled={!canCreate}
          title={canCreate ? "Save SMS Template" : "SmsTemplate_Create permission required"}
        >
          <FaPlus />
          Save SMS Template
        </button>
      </div>

      {message && <p className="biz-banner biz-banner-success">{message}</p>}

      <div className="biz-panel-search">
        <FaSearch className="biz-panel-search-icon" />
        <input
          type="search"
          placeholder="Search by name, message, or senderID"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
      </div>

      {error && !modalOpen && <p className="biz-panel-error">{error}</p>}

      <div className="biz-table-wrap">
        <table className="biz-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Template Name</th>
              <th>Sender ID</th>
              <th>Message</th>
              <th>Status</th>
              {canCreate && <th className="biz-col-actions">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={canCreate ? 6 : 5} className="biz-table-empty">
                  Loading templates…
                </td>
              </tr>
            ) : templates.length === 0 ? (
              <tr>
                <td colSpan={canCreate ? 6 : 5} className="biz-table-empty">
                  No SMS templates found
                </td>
              </tr>
            ) : (
              templates.map((row, idx) => (
                <tr key={row._id || idx}>
                  <td>{(page - 1) * limit + idx + 1}</td>
                  <td>{row.name || row.templateName || "—"}</td>
                  <td>{row.senderId || row.sender_id || "—"}</td>
                  <td className="biz-table-message">
                    {row.message || row.body || "—"}
                  </td>
                  <td>
                    <StatusCapsule value={row.status || "Active"} />
                  </td>
                  {canCreate && (
                    <td className="biz-col-actions">
                      <div className="biz-table-actions">
                        <button
                          type="button"
                          className="biz-btn-icon"
                          title="Edit"
                          onClick={() => openEdit(row)}
                        >
                          <FaEdit />
                        </button>
                        <button
                          type="button"
                          className="biz-btn-icon biz-btn-icon-danger"
                          title="Delete"
                          onClick={() => onDelete(row._id)}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="biz-pagination">
        <div>
          <span>Show:</span>
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
          >
            <option value={10}>10 / page</option>
            <option value={25}>25 / page</option>
            <option value={50}>50 / page</option>
          </select>
        </div>
        <div className="biz-pagination-right">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <span>
            Page {page} of {pagination.totalPages} ({pagination.total} total)
          </span>
          <button
            type="button"
            disabled={page >= pagination.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      </div>

      {modalOpen && (
        <div
          className="biz-modal-backdrop sms-template-modal-backdrop"
          onClick={closeModal}
          role="presentation"
        >
          <div
            className="biz-modal-card sms-template-modal"
            onClick={(ev) => ev.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="sms-template-modal-title"
          >
            <button
              type="button"
              className="sms-template-modal-close"
              onClick={closeModal}
              disabled={saving}
              aria-label="Close"
            >
              <FaTimes />
            </button>

            <h2 id="sms-template-modal-title" className="sms-template-modal-title">
              {editId ? "Edit SMS Template" : "Add SMS Template"}
            </h2>

            <form className="sms-template-modal-form" onSubmit={onSave}>
              {formError && <p className="biz-form-error">{formError}</p>}

              <label>
                Template Name
                <input
                  type="text"
                  name="templateName"
                  value={form.templateName}
                  onChange={onFieldChange}
                  placeholder="Enter the template name"
                  required
                />
              </label>

              <label>
                Sender ID
                <input
                  type="text"
                  name="senderid"
                  value={form.senderid}
                  onChange={onFieldChange}
                  placeholder="ENTER SENDER ID (E.G., ABCDEFGHIJ)"
                  className="sms-template-sender-input"
                  required
                />
              </label>

              <label>
                Provider
                <select
                  name="selectedProviderId"
                  value={form.selectedProviderId}
                  onChange={onFieldChange}
                  required
                >
                  <option value="">Select provider</option>
                  {providers.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.providerName || p.providername || p._id}
                    </option>
                  ))}
                </select>
              </label>

              {providers.length === 0 && (
                <p className="sms-template-provider-hint">
                  No SMS providers found.{" "}
                  {canOpenSmsConfig && projectId ? (
                    <>
                      Add one under{" "}
                      <Link
                        to={businessPath(projectId, "smsconfig")}
                        className="sms-template-config-link"
                        onClick={closeModal}
                      >
                        SMS Config
                      </Link>{" "}
                      first.
                    </>
                  ) : (
                    <>Add one under SMS Config first.</>
                  )}
                </p>
              )}

              <label>
                Template Message
                <textarea
                  name="templateMessage"
                  value={form.templateMessage}
                  onChange={onFieldChange}
                  placeholder="Enter the message (use #var# for placeholders)"
                  rows={6}
                  required
                />
              </label>

              <div className="sms-template-modal-actions">
                <button
                  type="button"
                  className="sms-template-btn-cancel"
                  onClick={closeModal}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="sms-template-btn-save"
                  disabled={saving}
                >
                  {saving ? "Saving…" : editId ? "Update Template" : "Save Template"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllSmsTemplate;
